import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Tab, Nav, Badge, Button, Modal, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

function Cards() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCard, setSelectedCard] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');
  
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Load cards from API
    const loadCards = async () => {
      try {
        setLoading(true);
        const { cards: cardList } = await api.getMyCards();
        setCards(cardList);
      } catch (error) {
        setError('Failed to load cards: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    loadCards();
  }, [currentUser, navigate]);

  // Group cards by category
  const categorizedCards = cards.reduce((acc, card) => {
    const category = card.category || 'other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(card);
    return acc;
  }, {});

  // Get cards for active category
  const displayCards = activeCategory === 'all' 
    ? cards 
    : categorizedCards[activeCategory] || [];

  const handleCardClick = (card) => {
    setSelectedCard(card);
    setShowModal(true);
  };

  const handleDeleteCard = async (cardId) => {
    if (window.confirm('Are you sure you want to delete this card?')) {
      try {
        await api.deleteCard(cardId);
        setShowModal(false);
        setSelectedCard(null);
        // Remove card from local state
        setCards(cards.filter(card => (card._id || card.id) !== cardId));
      } catch (err) {
        setError('Failed to delete card: ' + err.message);
      }
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      baseball: 'bi-circle-fill',
      basketball: 'bi-dribbble',
      football: 'bi-egg-fill',
      pokemon: 'bi-lightning-fill',
      magic: 'bi-stars',
      other: 'bi-collection'
    };
    return icons[category] || icons.other;
  };

  const getCategoryColor = (category) => {
    const colors = {
      baseball: 'danger',
      basketball: 'warning',
      football: 'success',
      pokemon: 'info',
      magic: 'secondary',
      other: 'dark'
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading your collection...</p>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Card className="shadow">
        <Card.Header className="bg-primary">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">
              <i className="bi bi-collection me-2"></i>
              My Card Collection
            </h2>
            <Button
              variant="success"
              onClick={() => navigate('/scan')}
              className="rounded-pill"
            >
              <i className="bi bi-plus-circle me-2"></i>
              Scan New Card
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
          
          {cards.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-inbox display-1 text-muted"></i>
              <h4 className="mt-3">No cards in your collection yet</h4>
              <p className="text-muted">Start by scanning your first trading card!</p>
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/scan')}
                className="mt-3 rounded-pill"
              >
                <i className="bi bi-camera me-2"></i>
                Scan Your First Card
              </Button>
            </div>
          ) : (
            <>
              <Tab.Container activeKey={activeCategory} onSelect={setActiveCategory}>
                <Nav variant="pills" className="mb-4">
                  <Nav.Item>
                    <Nav.Link eventKey="all" className="rounded-pill me-2">
                      All Cards
                      <Badge bg="secondary" className="ms-2">{cards.length}</Badge>
                    </Nav.Link>
                  </Nav.Item>
                  {Object.entries(categorizedCards).map(([category, categoryCards]) => (
                    <Nav.Item key={category}>
                      <Nav.Link eventKey={category} className="rounded-pill me-2">
                        <i className={`${getCategoryIcon(category)} me-1`}></i>
                        {category.charAt(0).toUpperCase() + category.slice(1)}
                        <Badge bg={getCategoryColor(category)} className="ms-2">
                          {categoryCards.length}
                        </Badge>
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>

                <Row className="g-4">
                  {displayCards.map((card) => (
                    <Col key={card._id || card.id} xs={12} sm={6} md={4} lg={3}>
                      <Card
                        className="h-100 border-0 shadow hover-shadow"
                        onClick={() => handleCardClick(card)}
                        style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                      >
                        <Card.Img
                          variant="top"
                          src={api.getImageUrl(card.imageUrl)}
                          alt={card.name}
                          style={{ height: '300px', objectFit: 'cover' }}
                        />
                        <Card.Body>
                          <Badge
                            bg={getCategoryColor(card.category || 'other')}
                            className="mb-2"
                          >
                            {card.type}
                          </Badge>
                          <h5 className="mb-1">{card.name}</h5>
                          <p className="text-muted small mb-2">
                            {card.year} {card.brand} #{card.cardNumber}
                          </p>
                          <h6 className="text-success mb-0">{card.estimatedValue}</h6>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Tab.Container>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Card Detail Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        className=""
      >
        {selectedCard && (
          <>
            <Modal.Header closeButton className="border-secondary">
              <Modal.Title>{selectedCard.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <img
                    src={api.getImageUrl(selectedCard.imageUrl)}
                    alt={selectedCard.name}
                    className="img-fluid rounded shadow"
                  />
                </Col>
                <Col md={6}>
                  <Card className="border-0 mb-3">
                    <Card.Body>
                      <h5 className="text-primary mb-3">Card Details</h5>
                      <Row className="g-2">
                        <Col xs={6}>
                          <small className="text-muted">Type</small>
                          <p className="mb-0 fw-bold">{selectedCard.type}</p>
                        </Col>
                        <Col xs={6}>
                          <small className="text-muted">Year</small>
                          <p className="mb-0 fw-bold">{selectedCard.year}</p>
                        </Col>
                        <Col xs={6}>
                          <small className="text-muted">Brand</small>
                          <p className="mb-0 fw-bold">{selectedCard.brand}</p>
                        </Col>
                        <Col xs={6}>
                          <small className="text-muted">Card #</small>
                          <p className="mb-0 fw-bold">{selectedCard.cardNumber}</p>
                        </Col>
                        <Col xs={12}>
                          <small className="text-muted">Condition</small>
                          <p className="mb-0 fw-bold">{selectedCard.condition}</p>
                        </Col>
                        {selectedCard.specialFeatures?.length > 0 && (
                          <Col xs={12}>
                            <small className="text-muted">Special Features</small>
                            <p className="mb-0 fw-bold">
                              {selectedCard.specialFeatures.join(', ')}
                            </p>
                          </Col>
                        )}
                      </Row>
                    </Card.Body>
                  </Card>

                  <Card className="border-0 mb-3">
                    <Card.Body>
                      <h5 className="text-success mb-3">Market Value</h5>
                      <h3>{selectedCard.estimatedValue}</h3>
                      {selectedCard.recentSales?.length > 0 && (
                        <div className="mt-3">
                          <small className="text-muted">Recent Sales:</small>
                          <ul className="list-unstyled mt-2">
                            {selectedCard.recentSales.map((sale, index) => (
                              <li key={index} className="text-success">
                                <i className="bi bi-check-circle me-2"></i>
                                {typeof sale === 'string' ? sale : `${sale.platform || 'Unknown'} - $${sale.price}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </Card.Body>
                  </Card>

                  {selectedCard.authenticity && (
                    <Card className={`bg-secondary border-2 ${
                      selectedCard.authenticity.includes('Authentic') 
                        ? 'border-success' 
                        : 'border-warning'
                    }`}>
                      <Card.Body>
                        <small className="text-muted">Authenticity</small>
                        <p className={`mb-0 fw-bold ${
                          selectedCard.authenticity.includes('Authentic')
                            ? 'text-success'
                            : 'text-warning'
                        }`}>
                          <i className={`bi ${
                            selectedCard.authenticity.includes('Authentic')
                              ? 'bi-shield-check'
                              : 'bi-shield-exclamation'
                          } me-2`}></i>
                          {selectedCard.authenticity}
                        </p>
                      </Card.Body>
                    </Card>
                  )}
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="border-secondary">
              <Button
                variant="danger"
                onClick={() => handleDeleteCard(selectedCard._id || selectedCard.id)}
              >
                <i className="bi bi-trash me-2"></i>
                Delete Card
              </Button>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </Container>
  );
}

export default Cards;